import { Box, Button, Checkbox, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  InputField,
  ProgramModelSelector,
  RoleColumnsHeader,
  CtaButton
} from './SettingsUI';
import { LoadingSpinner } from './LoadingSpinner';
import { SubjectModal } from './SubjectModal';
import {
  BasicPanel,
  DemographicPanel,
  PersonalPanel,
  CompanyPanel,
  SubjectsPanel
} from './panels';
import type { FieldSetting } from '../types';
import type { SubjectItem, LearningObjectivesState } from '../types';
import {
  AI_MATCHING_FIELD_OPTIONS,
  ALWAYS_ON_AI_FIELD_LABEL,
  AIMatchingFieldKey,
  AIMatchingFieldsConfig
} from '../../../../../Matches/aiMatchingFields';
import { useMemberWord } from '../../../../../../../../utils/memberTypeLabels';

interface ConfigurationViewProps {
  programName: string;
  setProgramName: (v: string) => void;
  model: string;
  setModel: (v: string) => void;
  participantLabel: string;
  setParticipantLabel: (v: string) => void;
  advisorLabel: string;
  setAdvisorLabel: (v: string) => void;
  maxMatchesPerMentee: number;
  setMaxMatchesPerMentee: (v: number) => void;
  maxMatchesPerMentor: number;
  setMaxMatchesPerMentor: (v: number) => void;
  sessionsPerMatch: number;
  setSessionsPerMatch: (v: number) => void;
  useAIMatching: boolean;
  setUseAIMatching: (v: boolean) => void;
  aiMatchingFields: AIMatchingFieldsConfig;
  setAiMatchingField: (key: AIMatchingFieldKey, value: boolean) => void;
  fieldSettings: Record<string, FieldSetting>;
  updateField: (
    key: string,
    role: 'mentee' | 'mentor' | 'admin' | 'mandatory',
    value: boolean
  ) => void;
  subjects: SubjectItem[];
  setSubjects: React.Dispatch<React.SetStateAction<SubjectItem[]>>;
  learningObjectives: LearningObjectivesState;
  setLearningObjectives: React.Dispatch<React.SetStateAction<LearningObjectivesState>>;
  basicSettingsLoading: boolean;
  learningObjectivesLoading: boolean;
  groupError: boolean;
  isProgramPublished: boolean;
  shouldDisableBasicFields: boolean;
  isMentorshipModel: boolean;
  isMasterclassModel: boolean;
  markUserEdited: () => void;
  onOpenSubjectModal: () => void;
  isSubjectModalOpen: boolean;
  newSubjectName: string;
  setNewSubjectName: (v: string) => void;
  newSubjectType: 'topic' | 'industry';
  setNewSubjectType: (v: 'topic' | 'industry') => void;
  onAddSubject: (subject: SubjectItem) => void;
  onCloseSubjectModal: () => void;
  // orgLevelAdmin: onboarding fields moved to org level, so hide the per-role
  // onboarding panels when this config surface is reused on Matches/Designer.
  hideOnboardingFields?: boolean;
}

export function ConfigurationView({
  hideOnboardingFields = false,
  programName,
  setProgramName,
  model,
  setModel,
  participantLabel,
  setParticipantLabel,
  advisorLabel,
  setAdvisorLabel,
  maxMatchesPerMentee,
  setMaxMatchesPerMentee,
  maxMatchesPerMentor,
  setMaxMatchesPerMentor,
  sessionsPerMatch,
  setSessionsPerMatch,
  useAIMatching,
  setUseAIMatching,
  aiMatchingFields,
  setAiMatchingField,
  fieldSettings,
  updateField,
  subjects,
  setSubjects,
  learningObjectives,
  setLearningObjectives,
  basicSettingsLoading,
  learningObjectivesLoading,
  groupError,
  isProgramPublished,
  shouldDisableBasicFields,
  isMentorshipModel,
  isMasterclassModel,
  markUserEdited,
  onOpenSubjectModal,
  isSubjectModalOpen,
  newSubjectName,
  setNewSubjectName,
  newSubjectType,
  setNewSubjectType,
  onAddSubject,
  onCloseSubjectModal
}: ConfigurationViewProps) {
  const w = useMemberWord();
  if (groupError) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 1,
          color: '#B6B6B6'
        }}
      >
        <Typography fontSize={13} color="#F16063">
          Could not load the program settings.
        </Typography>
      </Box>
    );
  }

  if (basicSettingsLoading) {
    return <LoadingSpinner minHeight={240} message="Loading settings..." />;
  }

  return (
    <>
      <Grid container spacing={0} sx={{ flex: 1, minHeight: 0, px: 2, overflow: 'hidden' }}>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            borderRight: '1px solid #2A2A2A',
            background: '#1C1C1C',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Box sx={{ flex: 1, px: 2, overflowY: 'auto', minHeight: 0 }}>
            <ProgramModelSelector
              value={model}
              disabled={isProgramPublished}
              onChange={(value) => {
                markUserEdited();
                setModel(value);
              }}
            />
            <Box>
              <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2, mt: 1 }}>
                User roles
              </Box>
              <Box sx={{ mb: 2 }}>
                <InputField
                  label="Label your Program Participant"
                  placeholder="Program Participant"
                  value={participantLabel}
                  onChange={(value) => {
                    markUserEdited();
                    setParticipantLabel(value);
                  }}
                />
              </Box>
              {model !== 'masterclass' && (
                <InputField
                  label="Label your Program Advisor"
                  placeholder="Program Advisor"
                  value={advisorLabel}
                  onChange={(value) => {
                    markUserEdited();
                    setAdvisorLabel(value);
                  }}
                />
              )}
            </Box>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={isMasterclassModel ? 9 : 6}
          sx={{
            background: '#1C1C1C',
            borderRight: isMasterclassModel ? '1px solid #2A2A2A' : 'none',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: '100%'
          }}
        >
          <Box sx={{ flex: 1, px: 3, py: 1, overflowY: 'auto', pr: 1, minHeight: 0 }}>
            {!hideOnboardingFields && (
              <>
                <Box sx={{ pb: 1, borderBottom: '1px solid #2A2A2A', mb: 2 }}>
                  <RoleColumnsHeader />
                </Box>
                <BasicPanel
                  fieldSettings={fieldSettings}
                  updateField={updateField}
                  disabled={shouldDisableBasicFields}
                />
                <DemographicPanel fieldSettings={fieldSettings} updateField={updateField} />
                <PersonalPanel fieldSettings={fieldSettings} updateField={updateField} />
                <CompanyPanel fieldSettings={fieldSettings} updateField={updateField} />
              </>
            )}
            <SubjectsPanel subjects={subjects} openModal={onOpenSubjectModal} />
          </Box>
        </Grid>

        {isMentorshipModel && (
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              background: '#1C1C1C',
              borderLeft: '1px solid #2A2A2A',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%'
            }}
          >
            <Box sx={{ flex: 1, px: 3, py: 2, overflowY: 'auto', pr: 1, minHeight: 0 }}>
              <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2 }}>
                Learning objectives
              </Box>
              <Typography
                sx={{ color: '#B6B6B6', fontSize: 12, mb: 2, lineHeight: '18px' }}
              >
                These entries will be displayed to {w('mentee', { plural: true, lower: true })} as Learning Objectives
                and {w('mentor', { plural: true, lower: true })} as Areas of Expertise to help matchmaking, so please
                use neutral language.
              </Typography>
              {learningObjectivesLoading ? (
                <LoadingSpinner minHeight={200} />
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {learningObjectives.objectives.map((obj, idx) => (
                      <InputField
                        key={idx}
                        label={`Objective ${idx + 1}`}
                        value={obj}
                        placeholder={`Objective ${idx + 1}`}
                        onChange={(v) => {
                          markUserEdited();
                          setLearningObjectives((prev) => {
                            const next = [...prev.objectives];
                            next[idx] = v;
                            return { ...prev, objectives: next, number: next.length || 1 };
                          });
                        }}
                      />
                    ))}
                    <Button
                      variant="outlined"
                      onClick={() => {
                        markUserEdited();
                        setLearningObjectives((prev) => ({
                          number: (prev.objectives.length || 0) + 1,
                          objectives: [...prev.objectives, '']
                        }));
                      }}
                      sx={{
                        color: '#FDFDFD',
                        borderColor: '#565656',
                        textTransform: 'none',
                        fontSize: 12,
                        alignSelf: 'flex-start'
                      }}
                    >
                      Add Objective
                    </Button>
                  </Box>
                  <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2, mt: 3 }}>
                    Mentorship
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <InputField
                      label={`Required Learning Objectives for ${w('mentee', { plural: true })} *`}
                      value={`${learningObjectives.number || 0}`}
                      onChange={(v) => {
                        markUserEdited();
                        setLearningObjectives((prev) => ({ ...prev, number: Number(v) || 0 }));
                      }}
                    />
                    <InputField
                      label="Maximum matches per participant *"
                      value={`${maxMatchesPerMentee}`}
                      onChange={(v) => {
                        markUserEdited();
                        setMaxMatchesPerMentee(Number(v) || 0);
                      }}
                    />
                    <InputField
                      label={`Maximum matches per ${w('mentor', { lower: true })} *`}
                      value={`${maxMatchesPerMentor}`}
                      onChange={(v) => {
                        markUserEdited();
                        setMaxMatchesPerMentor(Number(v) || 0);
                      }}
                    />
                    <InputField
                      label="Required sessions per match *"
                      value={`${sessionsPerMatch}`}
                      onChange={(v) => {
                        markUserEdited();
                        setSessionsPerMatch(Number(v) || 0);
                      }}
                    />
                  </Box>

                  <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, mb: 2, mt: 3 }}>
                    Matching with Elliott
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 1.5,
                      border: '1px solid #2A2A2A',
                      backgroundColor: useAIMatching ? '#F7942014' : 'transparent'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 18, color: useAIMatching ? '#F79420' : '#868686' }} />
                      <Box>
                        <Typography sx={{ fontSize: 13, color: '#FDFDFD', fontWeight: 500 }}>
                          Enable Matching with Elliott
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#868686', lineHeight: 1.3 }}>
                          Let Elliott find optimal matches based on profiles and goals
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={useAIMatching}
                      onChange={(_, checked) => {
                        markUserEdited();
                        setUseAIMatching(checked);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#F79420' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F79420' }
                      }}
                    />
                  </Box>

                  {useAIMatching && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid #2A2A2A',
                        backgroundColor: '#0F0F0F'
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: '#B6B6B6', mb: 1 }}>
                        Fields the AI considers in addition to <strong style={{ color: '#FDFDFD' }}>{ALWAYS_ON_AI_FIELD_LABEL}</strong> (always on):
                      </Typography>
                      <Grid container spacing={0} columns={{ xs: 1, sm: 2, md: 3 }}>
                        {AI_MATCHING_FIELD_OPTIONS.map((field) => (
                          <Grid item xs={1} key={field.key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!aiMatchingFields[field.key]}
                                  onChange={(_, checked) => setAiMatchingField(field.key, checked)}
                                  sx={{
                                    color: '#868686',
                                    '&.Mui-checked': { color: '#F79420' }
                                  }}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: 12, color: '#FDFDFD' }}>
                                  {field.label}
                                </Typography>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      <SubjectModal
        open={isSubjectModalOpen}
        onClose={onCloseSubjectModal}
        newSubjectName={newSubjectName}
        setNewSubjectName={setNewSubjectName}
        newSubjectType={newSubjectType}
        setNewSubjectType={setNewSubjectType}
        onAdd={onAddSubject}
      />
    </>
  );
}
